/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  EmailBlock,
  Inbox,
  Mail,
  PauseCircle,
  ViewportNarrow,
  ViewportWide,
} from '@saleshandy/icons';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import classNames from 'classnames';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import { Other } from '../../../../../../../../shared/svg/other';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../../../../shared/design-system/components/overlay';
import ProgressCard from '../../../../../../../../shared/components/sequence-progress-card/progress-card';
import { adjustOverlayPosition } from '../../../../../../../sequence/components/sequence-list-content/components/sequence-list-table/helper';
import { EspItemTable } from './components/esp-item-table';
import SpamAssassinReportModal from './components/spam-assassin-report-modal';
import {
  SeedAccountImages,
  SeedAccountLabels,
} from '../../../../../test-create-update/components/seed-esp-account/type';
import {
  SeedTestSenderEmailAccountPauseReason,
  SeedTestSenderEmailAccountPauseReasonMessages,
  SpamAssassinScoreReportFilter,
  TestStatus,
  TestType,
} from '../../../../../../enums/inbox-radar';
import { getTooltipText } from '../../../../../../../sequence/components/sequence-single-content/components/sequence-single-header-actions/utils/helper';
import {
  SpamAssassinScore,
  TestConfigResponse,
  TestSummaryResponse,
} from '../../../../../../types';
import { useFirebaseAnimation } from '../utils/use-firebase-animation';

const SenderEmailAccounts: React.FC<{
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  testReportSenderEmailAccountList: any;
  hasMoreSenderEmailAccounts: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  forShareableReport?: boolean;
  configType?: TestType;
  inboxPlacement?: TestSummaryResponse;
  testId?: string;
  sendGetSpamAssassinReportRequest?: (
    seedTestId: string,
    payload: { seedTestSenderEmailAccountId: number },
    filter?: SpamAssassinScoreReportFilter,
  ) => void;
  spamAssassinReportResponse?: any;
  isLoadingSpamAssassinReport?: boolean;
  testConfigData?: TestConfigResponse;
  firebaseData?: any;
}> = ({
  scrollContainerRef,
  testReportSenderEmailAccountList,
  hasMoreSenderEmailAccounts,
  onLoadMore,
  isLoadingMore,
  forShareableReport = false,
  configType,
  isLoadingSpamAssassinReport,
  testConfigData,
  firebaseData,
}) => {
  const { t } = useTranslation();
  const scrollHandlerRef = useRef<() => void>();
  const [
    showSpamAssassinReportModal,
    setShowSpamAssassinReportModal,
  ] = useState(false);
  const [
    spamAssassinReportModalData,
    setSpamAssassinReportModalData,
  ] = useState<SpamAssassinScore>(null);
  const [selectedEmailAccountId, setSelectedEmailAccountId] = useState<number>(
    null,
  );
  const [currentFromEmail, setCurrentFromEmail] = useState<string>('');
  const [hoveredEmailAccount, setHoveredEmailAccount] = useState<string | null>(
    null,
  );

  const listItemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [expandedEmailAccounts, setExpandedEmailAccounts] = useState<string[]>(
    testReportSenderEmailAccountList?.length === 1
      ? [testReportSenderEmailAccountList[0].fromEmail]
      : [],
  );

  const [expandedESPItems, setExpandedESPItems] = useState<
    Record<string, string[]>
  >({});

  const [openESPBreakDownOverlay, setOpenESPBreakDownOverlay] = useState({
    itemId: '',
    type: '',
  });

  // Use Firebase animation hook
  const { shouldAnimateItem } = useFirebaseAnimation(firebaseData, {
    duration: 2000,
    fields: ['inboxCount', 'spamCount', 'otherCount', 'notDeliveredCount'],
  });

  const prevExpandedEmailAccounts = useRef<string[]>(
    testReportSenderEmailAccountList?.length === 1
      ? [testReportSenderEmailAccountList[0].fromEmail]
      : [],
  );

  // Initialize first ESP item as expanded by default
  useEffect(() => {
    if (testReportSenderEmailAccountList?.length > 0) {
      const initialExpandedESP: Record<string, string[]> = {};
      testReportSenderEmailAccountList.forEach((emailAccount) => {
        if (emailAccount.espData?.length > 0) {
          initialExpandedESP[emailAccount.fromEmail] = [
            emailAccount.espData[0].type,
          ];
        }
      });
      setExpandedESPItems(initialExpandedESP);
    }
  }, [testReportSenderEmailAccountList]);

  const isESPItemExpanded = (emailAccountId: string, espType: string) =>
    expandedESPItems[emailAccountId]?.includes(espType) || false;

  const toggleESPItemExpansion = (emailAccountId: string, espType: string) => {
    setExpandedESPItems((prev) => {
      const currentExpanded = prev[emailAccountId] || [];
      const isCurrentlyExpanded = currentExpanded.includes(espType);

      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [emailAccountId]: currentExpanded.filter((type) => type !== espType),
        };
      }
      return {
        ...prev,
        [emailAccountId]: [...currentExpanded, espType],
      };
    });
  };

  const getEmailTypeIcon = (emailType: string) => {
    if (SeedAccountImages[emailType]) {
      return (
        <img
          src={SeedAccountImages[emailType]}
          alt={emailType}
          width={16}
          height={16}
        />
      );
    }
    return <Other height={16} width={16} />;
  };

  const getEmailTypeLabel = (emailType: string) =>
    SeedAccountLabels[emailType] || 'Other';

  const getESPBreakdownByType = (espBreakdownByType: any) => {
    if (!espBreakdownByType) {
      return [];
    }
    const data = espBreakdownByType.map((item: any) => ({
      title: getEmailTypeLabel(item.esp),
      icon: getEmailTypeIcon(item.esp),
      score: item.count,
      percentage: item.percentage,
    }));
    return data || [];
  };

  const placementItemsConfig = [
    {
      type: 'inbox',
      label: 'Inbox',
      IconComponent: Inbox,
      dataKey: 'inbox',
      espBreakdownDataKey: 'inbox',
      classNameSuffix: 'inbox-placement',
    },
    {
      type: 'spam',
      label: 'Spam',
      IconComponent: AlertTriangle,
      dataKey: 'spam',
      espBreakdownDataKey: 'spam', // Assuming 'spam' key exists in item.espBreakdownByType
      classNameSuffix: 'spam-placement',
    },
    {
      type: 'other',
      label: 'Other',
      IconComponent: Mail,
      dataKey: 'other',
      espBreakdownDataKey: 'other', // Assuming 'other' key exists in item.espBreakdownByType
      classNameSuffix: 'other-placement',
    },
    {
      type: 'notDelivered',
      label: 'Undelivered',
      IconComponent: EmailBlock,
      dataKey: 'notDelivered',
      espBreakdownDataKey: 'notDelivered', // Assuming 'notDelivered' key exists in item.espBreakdownByType
      classNameSuffix: 'not-delivered-placement',
    },
  ];

  // Helper functions to extract Firebase data logic
  const getFirebaseItemData = (seedTestSenderEmailAccountId: number) => {
    const firebaseIds = firebaseData ? Object.keys(firebaseData) : [];
    return firebaseIds.includes(seedTestSenderEmailAccountId?.toString())
      ? firebaseData[seedTestSenderEmailAccountId]
      : null;
  };

  const getPausedReason = (item: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );
    const pausedReason =
      (firebaseItemData?.pausedReasonIdentifier &&
        firebaseItemData.pausedReasonIdentifier !==
          SeedTestSenderEmailAccountPauseReason.ManuallyPausedByUser) ||
      (item.pausedReasonIdentifier &&
        item.pausedReasonIdentifier !==
          SeedTestSenderEmailAccountPauseReason.ManuallyPausedByUser);

    const pausedReasonIdentifierText =
      firebaseItemData?.pausedReasonIdentifier || item.pausedReasonIdentifier;

    return { pausedReason, pausedReasonIdentifierText };
  };

  const getTotalSentCount = (item: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );
    if (firebaseItemData?.placementCount !== undefined) {
      return firebaseItemData.placementCount;
    }
    // Fallback to current data
    return item?.espData?.reduce(
      (total, esp) => total + (esp?.emailSent?.value || 0),
      0,
    );
  };

  const getTotalCount = (item: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );
    if (firebaseItemData?.totalCount !== undefined) {
      return firebaseItemData.totalCount;
    }
    // Fallback to current data
    return item?.espData?.reduce(
      (total, esp) => total + (esp?.emailSent?.totalEmails || 0),
      0,
    );
  };

  const getPlacementPercentage = (item: any, pItem: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );
    const firebasePlacementSummary = firebaseItemData?.placementSummary;

    if (
      firebasePlacementSummary &&
      firebaseItemData?.placementCount !== undefined
    ) {
      // If placementCount is 0, return a special indicator for spinner
      if (firebaseItemData.placementCount === 0) {
        return 'loading';
      }

      const firebaseKey =
        pItem.dataKey === 'inbox'
          ? 'inboxCount'
          : pItem.dataKey === 'spam'
          ? 'spamCount'
          : pItem.dataKey === 'other'
          ? 'otherCount'
          : 'notDeliveredCount';

      const total = firebasePlacementSummary[firebaseKey]?.total || 0;

      // Calculate sum of all keys in firebasePlacementSummary
      const sumOfAllKeys =
        firebasePlacementSummary?.inboxCount?.total +
        firebasePlacementSummary?.spamCount?.total +
        firebasePlacementSummary?.otherCount?.total +
        firebasePlacementSummary?.notDeliveredCount?.total;

      const percentage = Math.round((total / sumOfAllKeys) * 100);

      // Check if this placement should be animated
      const shouldAnimate = shouldAnimateItem(
        `${item.seedTestSenderEmailAccountId}-${firebaseKey}`,
      );

      return {
        percentage,
        shouldAnimate,
      };
    }
    // Fallback to current data
    const percentage =
      item[pItem.dataKey as 'inbox' | 'spam' | 'other' | 'notDelivered']
        ?.percentage || 0;

    return {
      percentage,
      shouldAnimate: false,
    };
  };

  const getESPBreakdownData = (item: any, pItem: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );
    const firebaseKey =
      pItem.dataKey === 'inbox'
        ? 'inboxCount'
        : pItem.dataKey === 'spam'
        ? 'spamCount'
        : pItem.dataKey === 'other'
        ? 'otherCount' // Fixed: was 'othersCount', should be 'otherCount'
        : 'notDeliveredCount';

    if (firebaseItemData?.placementSummary?.[firebaseKey]?.espWiseBreakdown) {
      // Convert Firebase espWiseBreakdown to the format expected by ProgressCard
      const espBreakdown =
        firebaseItemData.placementSummary[firebaseKey]?.espWiseBreakdown;
      const total = firebaseItemData.placementSummary[firebaseKey]?.total || 0;
      return Object.entries(espBreakdown).map(([esp, count]) => ({
        title: getEmailTypeLabel(esp),
        icon: getEmailTypeIcon(esp),
        score: count,
        percentage:
          total > 0 ? Math.round(((count as number) / total) * 100) : 0,
      }));
    }
    // Fallback to current data
    return getESPBreakdownByType(
      item.espBreakdownByType?.[pItem.espBreakdownDataKey],
    );
  };

  const getESPItemTotalCount = (item: any, espItem: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );
    const firebaseEspData =
      firebaseItemData?.seedAccountEspWise?.[espItem.type];

    if (firebaseEspData?.seedPlacements) {
      // Calculate count from ESP item table rows
      return Object.keys(firebaseEspData.seedPlacements).length;
    }
    // Fallback to current data
    return espItem?.emailSent?.totalEmails;
  };

  const getESPItemPercentage = (item: any, espItem: any, pItem: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );

    // If placementCount is 0, return a special indicator for spinner
    if (firebaseItemData?.placementCount === 0) {
      return 'loading';
    }

    const firebaseEspData =
      firebaseItemData?.seedAccountEspWise?.[espItem.type];

    if (firebaseEspData?.placementSummary && firebaseItemData?.totalCount) {
      const firebaseKey =
        pItem.dataKey === 'inbox'
          ? 'inboxCount'
          : pItem.dataKey === 'spam'
          ? 'spamCount'
          : pItem.dataKey === 'other'
          ? 'otherCount' // Fixed: was 'othersCount', should be 'otherCount'
          : 'notDeliveredCount';

      const count = firebaseEspData.placementSummary[firebaseKey] || 0;
      const totalCount = firebaseEspData.sentCount;

      const percentage =
        totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;

      // Check if this ESP item should be animated
      const shouldAnimate = shouldAnimateItem(
        `${item.seedTestSenderEmailAccountId}-${espItem.type}-${firebaseKey}`,
      );

      return {
        percentage,
        shouldAnimate,
      };
    }

    // Fallback to current data
    const percentage =
      espItem[pItem.dataKey as 'inbox' | 'spam' | 'other' | 'notDelivered']
        ?.percentage || 0;

    return {
      percentage,
      shouldAnimate: false,
    };
  };

  const shouldShowQuotaWarning = (item: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );
    return (
      item.availableQuota <= '0' &&
      !(
        firebaseItemData?.pausedReasonIdentifier || item.pausedReasonIdentifier
      ) &&
      testConfigData.testStatus === TestStatus.Active
    );
  };

  const renderPausedReasonIcon = (item: any) => {
    const { pausedReason, pausedReasonIdentifierText } = getPausedReason(item);

    if (pausedReason) {
      return (
        <OverlayTooltip
          text={getTooltipText(
            SeedTestSenderEmailAccountPauseReasonMessages[
              pausedReasonIdentifierText
            ],
            t,
          )}
          placement={Placement.Auto}
        >
          <span className="sender-email-account-paused-reason">
            <AlertTriangle color="#B91C1C" />
          </span>
        </OverlayTooltip>
      );
    }
    return null;
  };

  const hasESPBreakdownData = (item: any, pItem: any) => {
    const firebaseItemData = getFirebaseItemData(
      item.seedTestSenderEmailAccountId,
    );
    const firebasePlacementSummary = firebaseItemData?.placementSummary;
    const firebaseKey =
      pItem.dataKey === 'inbox'
        ? 'inboxCount'
        : pItem.dataKey === 'spam'
        ? 'spamCount'
        : pItem.dataKey === 'other'
        ? 'otherCount' // Fixed: was 'othersCount', should be 'otherCount'
        : 'notDeliveredCount';

    return (
      (firebasePlacementSummary?.[firebaseKey]?.espWiseBreakdown &&
        firebasePlacementSummary?.[firebaseKey]?.total > 0) ||
      (item.espBreakdownByType?.[pItem.espBreakdownDataKey] &&
        item.espBreakdownByType?.[pItem.espBreakdownDataKey]?.length > 0)
    );
  };

  const renderESPItemPercentage = (item: any, espItem: any, pItem: any) => {
    const espItemData = getESPItemPercentage(item, espItem, pItem);

    if (espItemData === 'loading') {
      return <Spinner animation="border" size="sm" />;
    }

    return (
      <span
        className={
          espItemData.shouldAnimate
            ? `esp-count-pop-animation ${pItem.classNameSuffix}`
            : ''
        }
      >
        {espItemData.percentage}%
      </span>
    );
  };

  const handleScroll = useCallback(() => {
    if (scrollContainerRef?.current) {
      const {
        scrollTop,
        scrollHeight,
        clientHeight,
      } = scrollContainerRef.current;
      // Load more when the user is 200px from the bottom
      if (scrollHeight - scrollTop <= clientHeight + 10) {
        if (hasMoreSenderEmailAccounts && !isLoadingMore) {
          onLoadMore();
        }
      }
    }
    // Explicitly return undefined to satisfy some linter configurations if needed,
    // though for event handlers, this is usually not necessary.
    return undefined;
  }, [hasMoreSenderEmailAccounts, isLoadingMore, onLoadMore]);

  scrollHandlerRef.current = handleScroll;

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (container) {
      const scrollHandler = () => scrollHandlerRef.current?.();
      container.addEventListener('scroll', scrollHandler);
      return () => container.removeEventListener('scroll', scrollHandler);
    }
    return undefined; // Ensure useEffect cleanup also has a consistent return for linters
  }, [scrollContainerRef]);

  useEffect(() => {
    if (openESPBreakDownOverlay.itemId && openESPBreakDownOverlay.type) {
      setTimeout(() => {
        adjustOverlayPosition(`.sender-email-accounts-esp-popover-content`, {
          shouldAdjustHorizontally: true,
        });
      }, 0);
    }
  }, [openESPBreakDownOverlay]);

  useEffect(() => {
    const newlyExpandedAccountId = expandedEmailAccounts.find(
      (id) => !prevExpandedEmailAccounts.current.includes(id),
    );

    if (newlyExpandedAccountId) {
      const element = listItemRefs.current[newlyExpandedAccountId];
      const container = scrollContainerRef?.current;
      if (element && container) {
        setTimeout(() => {
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          // We want the element's top to be 180px below the viewport top.
          // This is the distance we need to scroll the container by.
          const offset = configType === TestType.RECURRING ? 180 : 146;
          const scrollOffset = elementRect.top - containerRect.top - offset;

          container.scrollBy({
            top: scrollOffset,
            behavior: 'smooth',
          });
        }, 100);
      }
    }

    prevExpandedEmailAccounts.current = expandedEmailAccounts;
  }, [expandedEmailAccounts]);

  return (
    <>
      <div
        className={classNames(
          'sender-email-accounts-container',
          forShareableReport && 'shareable-report',
        )}
      >
        <div className="sender-email-accounts-header">
          <div className="sender-email-accounts-header-title">
            Sender Email Accounts
          </div>
          {testReportSenderEmailAccountList?.length > 0 && (
            <div
              className="sender-email-accounts-header-actions"
              {...accessibleOnClick(() => {
                if (
                  expandedEmailAccounts.length ===
                  testReportSenderEmailAccountList?.length
                ) {
                  setExpandedEmailAccounts([]);
                } else {
                  setExpandedEmailAccounts(
                    testReportSenderEmailAccountList?.map(
                      (item) => item.fromEmail,
                    ),
                  );
                }
              })}
            >
              <span className="sender-email-accounts-header-actions-icon">
                {expandedEmailAccounts.length ===
                testReportSenderEmailAccountList?.length ? (
                  <ViewportNarrow />
                ) : (
                  <ViewportWide />
                )}
              </span>
              <span>
                {expandedEmailAccounts.length ===
                testReportSenderEmailAccountList?.length
                  ? 'Collapse All'
                  : 'Expand All'}
              </span>
            </div>
          )}
        </div>
        {testReportSenderEmailAccountList?.length > 0 && (
          <div className="sender-email-accounts-list">
            {testReportSenderEmailAccountList?.length > 0 &&
              testReportSenderEmailAccountList?.map((item) => (
                <div
                  ref={(el) => {
                    listItemRefs.current[item.fromEmail] = el;
                  }}
                  key={item.fromEmail}
                  className={`sender-email-accounts-list-item ${
                    expandedEmailAccounts.includes(item.fromEmail)
                      ? 'expanded'
                      : ''
                  }`}
                >
                  <div
                    className="sender-email-accounts-list-item-header height-55"
                    onMouseEnter={() => setHoveredEmailAccount(item.fromEmail)}
                    onMouseLeave={() => setHoveredEmailAccount(null)}
                  >
                    <div
                      className="sender-email-account-email-container"
                      {...accessibleOnClick(() => {
                        if (expandedEmailAccounts.includes(item.fromEmail)) {
                          setExpandedEmailAccounts(
                            expandedEmailAccounts.filter(
                              (id) => id !== item.fromEmail,
                            ),
                          );
                        } else {
                          setExpandedEmailAccounts([
                            ...expandedEmailAccounts,
                            item.fromEmail,
                          ]);
                        }
                      })}
                    >
                      <span className="sender-email-account-expend-icon">
                        {expandedEmailAccounts.includes(item.fromEmail) ? (
                          <ChevronDown height={20} width={20} />
                        ) : (
                          <ChevronRight height={20} width={20} />
                        )}
                      </span>
                      <div className="sender-email-account-email">
                        <span className="sender-email-account-type">
                          {getEmailTypeIcon(item.emailType)}
                        </span>
                        <span className="sender-email-account-email-address">
                          {item.fromEmail}
                        </span>

                        {renderPausedReasonIcon(item)}
                        {shouldShowQuotaWarning(item) && (
                          <OverlayTooltip
                            text="You've reached the daily sending limit. Remaining emails will be sent tomorrow, and placement results will update automatically."
                            placement={Placement.Bottom}
                          >
                            <PauseCircle
                              width={16}
                              height={16}
                              color="#D97706"
                            />
                          </OverlayTooltip>
                        )}
                        {hoveredEmailAccount === item.fromEmail && (
                          <OverlayTooltip
                            text="Total Email Sent"
                            placement={Placement.Bottom}
                          >
                            <div className="total-sent-count">
                              <span className="total-sent">
                                {getTotalSentCount(item)}
                              </span>
                              <span className="total-count">
                                /{getTotalCount(item)}
                              </span>
                            </div>
                          </OverlayTooltip>
                        )}
                      </div>
                    </div>
                    <div className="sender-email-accounts-list-item-header-inbox-placement">
                      {placementItemsConfig.map((pItem, index) => {
                        const placementData = getPlacementPercentage(
                          item,
                          pItem,
                        );

                        return (
                          <React.Fragment key={pItem.type}>
                            <OverlayPopover
                              show={
                                openESPBreakDownOverlay.itemId ===
                                  item.fromEmail &&
                                openESPBreakDownOverlay.type === pItem.type &&
                                hasESPBreakdownData(item, pItem)
                              }
                              onToggle={(showPopover: boolean) => {
                                if (!showPopover) {
                                  setOpenESPBreakDownOverlay({
                                    itemId: '',
                                    type: '',
                                  });
                                }
                              }}
                              placement={Placement.BottomStart}
                              rootClose={true}
                              className="sender-email-accounts-esp-popover"
                              content={
                                <ProgressCard
                                  data={getESPBreakdownData(item, pItem)}
                                  borderClassName="#FCA5A5" // Keeping border color consistent as per original
                                  isLoading={false}
                                  className="sender-email-accounts-esp-popover-content"
                                  handleHideOverlayCard={() => {
                                    setOpenESPBreakDownOverlay({
                                      itemId: '',
                                      type: '',
                                    });
                                  }}
                                />
                              }
                            >
                              <div
                                className={`inbox-placement-item ${pItem.classNameSuffix}`}
                                {...accessibleOnClick(() => {
                                  setOpenESPBreakDownOverlay({
                                    itemId: item.fromEmail,
                                    type: pItem.type,
                                  });
                                })}
                              >
                                <div className="inbox-placement-item-name">
                                  <pItem.IconComponent height={16} width={16} />
                                  <span>{pItem.label}</span>
                                </div>
                                <div
                                  className={`inbox-placement-item-value ${
                                    placementData !== 'loading' &&
                                    placementData.shouldAnimate
                                      ? `esp-count-pop-animation ${pItem.classNameSuffix}`
                                      : ''
                                  }`}
                                >
                                  {placementData === 'loading' ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    `${placementData.percentage}%`
                                  )}
                                </div>
                              </div>
                            </OverlayPopover>
                            {index < placementItemsConfig.length - 1 && (
                              <div className="inbox-placement-item-divider" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  {expandedEmailAccounts.includes(item.fromEmail) && (
                    <div className="sender-email-accounts-list-item-content">
                      <div className="sender-email-account-esp-list-container">
                        <div className="sender-email-account-esp-list-header">
                          <span>Email Service Provider Placement Report</span>
                        </div>
                        <div className="sender-email-account-esp-list">
                          {item.espData?.map((espItem) => (
                            <div className="sender-email-account-esp-item">
                              <div className="sender-email-accounts-list-item-header">
                                <div
                                  className="sender-email-account-email-container gap-8"
                                  {...accessibleOnClick(() => {
                                    toggleESPItemExpansion(
                                      item.fromEmail,
                                      espItem.type,
                                    );
                                  })}
                                >
                                  <span className="sender-email-account-expend-icon">
                                    {isESPItemExpanded(
                                      item.fromEmail,
                                      espItem.type,
                                    ) ? (
                                      <ChevronDown height={20} width={20} />
                                    ) : (
                                      <ChevronRight height={20} width={20} />
                                    )}
                                  </span>
                                  <div className="sender-email-account-email">
                                    <span className="sender-email-account-type">
                                      {getEmailTypeIcon(espItem.type)}
                                    </span>
                                    <span className="sender-email-account-email-address font-size-14">
                                      {getEmailTypeLabel(espItem.type)}
                                    </span>
                                    <span className="sender-email-account-total-count">
                                      {getESPItemTotalCount(item, espItem)}
                                    </span>
                                  </div>
                                </div>
                                <div className="sender-email-accounts-list-item-header-inbox-placement">
                                  {placementItemsConfig.map((pItem, index) => (
                                    <React.Fragment key={pItem.type}>
                                      <div
                                        className={`inbox-placement-item cursor-text ${pItem.classNameSuffix}`}
                                      >
                                        <div className="inbox-placement-item-name">
                                          <pItem.IconComponent
                                            height={16}
                                            width={16}
                                          />
                                          <span>{pItem.label}</span>
                                        </div>
                                        <div className="inbox-placement-item-value">
                                          {renderESPItemPercentage(
                                            item,
                                            espItem,
                                            pItem,
                                          )}
                                        </div>
                                      </div>
                                      {index <
                                        placementItemsConfig.length - 1 && (
                                        <div className="inbox-placement-item-divider" />
                                      )}
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                              {isESPItemExpanded(
                                item.fromEmail,
                                espItem.type,
                              ) && (
                                <EspItemTable
                                  data={espItem?.emailList}
                                  espType={espItem.type}
                                  firebaseData={firebaseData}
                                  seedTestSenderEmailAccountId={
                                    item?.seedTestSenderEmailAccountId
                                  }
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            {isLoadingMore && (
              <div className="sender-email-accounts-list-loading">
                <Spinner className="spinner--blue" animation="border" />
              </div>
            )}
            {hasMoreSenderEmailAccounts === false &&
              testReportSenderEmailAccountList?.length > 15 && (
                <div className="end-list-divider">
                  <span>The End</span>
                </div>
              )}
          </div>
        )}
      </div>

      <SpamAssassinReportModal
        show={showSpamAssassinReportModal}
        onClose={() => {
          setShowSpamAssassinReportModal(false);
          setCurrentFromEmail('');
        }}
        data={spamAssassinReportModalData}
        emailAccounts={testReportSenderEmailAccountList?.map((item) => ({
          id: item.id,
          fromEmail: item.fromEmail,
        }))}
        selectedEmailAccountId={selectedEmailAccountId}
        onEmailAccountChange={setSelectedEmailAccountId}
        keySummaryModal={true}
        isLoading={isLoadingSpamAssassinReport}
        isFromEmail={currentFromEmail}
      />
    </>
  );
};

export default SenderEmailAccounts;
